import * as THREE from "three";
import { defineModel, smokePlume, type Part } from "../../lib";

// Mushroom House — a storybook toadstool cottage: a fat spotted red mushroom
// cap swallowing a plump cream stalk house, a round green door, tiny porthole
// windows and a stubby stone chimney poking out the side with puffing smoke.
// The cap (with its spots and gills) is named "cap" so the whole crown can
// breathe — a slow, gentle squash-and-stretch wobble about the stalk top —
// while the smoke puffs ride their own looping plume.
//
// Frame: y = 0 is the ground, the door faces +Z. 1 unit = 1 m; the house is a
// squat ~3.5 m tall storybook caricature on a 2x2 tile plot.

const CAP = "#d8412f";        // saturated toadstool red
const CAP_DARK = "#a92e22";   // gill underside / shaded rim
const STALK = "#f6efe3";      // plump cream stalk walls
const STALK_DARK = "#e4d9c4"; // shaded stalk ring at the base
const SPOT = "#f6efe3";       // cream cap spots
const DOOR = "#5f8f3e";       // round leafy-green door
const DOOR_DARK = "#4a7230";  // door recess ring
const WOOD = "#8a5a33";       // window frames / door step
const GLASS = "#ffd98a";      // warm lit window panes
const STONE = "#9a938a";      // chimney stones
const KNOB = "#e8b53a";       // golden door knob

// ── frame heights ────────────────────────────────────────────────────────────
const STALK_H = 1.9;                 // stalk walls, ground to cap underside
const CAP_Y = STALK_H + 0.15;        // cap centre sits just above the stalk top
const CAP_R = 1.85;                  // cap radius — deliberately swollen
const CAP_SQUASH = 0.62;             // cap is a flattened dome, not a ball

// The cap breathes about the point where it seats on the stalk.
const CAP_PIVOT: [number, number, number] = [0, STALK_H, 0];

// The chimney mouth the smoke rises from — out past the cap rim on the right,
// so the pipe and its puffs read clear of the big red dome.
const CHIMNEY_MOUTH: [number, number, number] = [2.0, 2.55, -0.3];

// Smoke puffs sized for the metre scale (the effect defaults are authored for
// much smaller models).
const smoke = smokePlume(CHIMNEY_MOUTH, {
  puffs: 4,
  rise: 1.3,
  drift: [0.3, -0.2],
  radius: 0.16,
  color: "#d9d4cc",
  duration: 4.8,
});

// Cap spots: flattened cream domes pressed into the red cap surface. Each spot
// sits on the (squashed) cap sphere along its own direction, so it hugs the
// dome. All named "cap" so they wobble with the crown.
function capSpots(): Part[] {
  const spots: Array<[number, number, number]> = [
    // [azimuth (rad), elevation 0..1 up the dome, spot radius]
    [0.4, 0.55, 0.30],
    [1.7, 0.35, 0.24],
    [2.9, 0.6, 0.28],
    [4.1, 0.4, 0.22],
    [5.3, 0.5, 0.26],
    [0.9, 0.85, 0.2],
    [3.6, 0.85, 0.22],
  ];
  return spots.map(([az, el, r]) => {
    const horiz = Math.cos(el * Math.PI / 2);
    const x = Math.cos(az) * CAP_R * 0.92 * horiz;
    const z = Math.sin(az) * CAP_R * 0.92 * horiz;
    const y = CAP_Y + Math.sin(el * Math.PI / 2) * CAP_R * CAP_SQUASH * 0.92;
    return {
      geometry: new THREE.SphereGeometry(r, 10, 8),
      color: SPOT,
      position: [x, y, z],
      scale: [1, 0.45, 1],
      name: "cap",
    } satisfies Part;
  });
}

// A tiny porthole window: a wooden ring with a warm lit pane, pressed into the
// stalk wall along direction `az` at height `y`.
function porthole(az: number, y: number, r: number): Part[] {
  // Stalk surface radius at window height, plus a nudge so the ring stands
  // proud of the wall instead of sinking flush into it.
  const wallR = 1.45 - (y / STALK_H) * 0.3 + 0.04;
  const x = Math.sin(az) * wallR;
  const z = Math.cos(az) * wallR;
  const rot: [number, number, number] = [Math.PI / 2, az, 0];
  return [
    { geometry: new THREE.CylinderGeometry(r, r, 0.18, 12), color: WOOD,
      position: [x, y, z], rotation: rot, eulerOrder: "YXZ" },
    { geometry: new THREE.CylinderGeometry(r * 0.68, r * 0.68, 0.22, 12), color: GLASS,
      position: [x, y, z], rotation: rot, eulerOrder: "YXZ" },
  ];
}

export const mushroomHouse = defineModel({
  id: "mushroom-house",
  name: "Mushroom House",
  batchId: "mess",
  tags: ["mess", "cute", "animated", "building", "fantasy", "mushroom", "cottage", "house"],
  summary: "A plump toadstool cottage with a spotted red cap, round door and puffing side chimney.",
  description:
    "A storybook mushroom cottage: a swollen spotted red toadstool cap over a " +
    "fat cream stalk, with a round leafy-green door, tiny porthole windows and " +
    "a stubby stone chimney puffing smoke out the side. The cap breathes with " +
    "a gentle squash-and-stretch wobble while the smoke drifts upward.",
  author: "models",
  footprint: 2.1,
  tiles: { w: 2, h: 2 },
  parts(): Part[] {
    return [
      // ── stalk house ───────────────────────────────────────────────────────
      // A plump cream stalk, wider at the base so it reads planted.
      { geometry: new THREE.CylinderGeometry(1.15, 1.45, STALK_H, 18), color: STALK,
        position: [0, STALK_H / 2, 0] },
      // A shaded ring skirting the base.
      { geometry: new THREE.CylinderGeometry(1.5, 1.58, 0.24, 18), color: STALK_DARK,
        position: [0, 0.12, 0] },

      // ── the cap (all named "cap" so the crown breathes as one) ────────────
      // The big squashed red dome.
      { geometry: new THREE.SphereGeometry(CAP_R, 20, 14), color: CAP,
        position: [0, CAP_Y, 0], scale: [1, CAP_SQUASH, 1], name: "cap" },
      // Gill underside: a dark red plate closing the cap's rim from below.
      { geometry: new THREE.CylinderGeometry(1.72, 1.3, 0.3, 18), color: CAP_DARK,
        position: [0, CAP_Y - 0.12, 0], name: "cap" },
      ...capSpots(),

      // ── the round door (facing +Z) ────────────────────────────────────────
      // Recess ring, then the green door disc, both pressed into the stalk.
      { geometry: new THREE.CylinderGeometry(0.72, 0.72, 0.16, 16), color: DOOR_DARK,
        position: [0, 0.78, 1.28], rotation: [Math.PI / 2, 0, 0] },
      { geometry: new THREE.CylinderGeometry(0.6, 0.6, 0.2, 16), color: DOOR,
        position: [0, 0.78, 1.3], rotation: [Math.PI / 2, 0, 0] },
      // Golden knob, off-centre like a real round door.
      { geometry: new THREE.SphereGeometry(0.08, 8, 6), color: KNOB,
        position: [0.36, 0.78, 1.42] },
      // A chunky wooden doorstep.
      { geometry: new THREE.BoxGeometry(1.1, 0.14, 0.5), color: WOOD,
        position: [0, 0.07, 1.4] },

      // ── tiny porthole windows ─────────────────────────────────────────────
      ...porthole(Math.PI / 3.2, 1.15, 0.24),     // front-right
      ...porthole(-Math.PI / 3.2, 1.15, 0.24),    // front-left
      ...porthole(Math.PI, 1.1, 0.26),            // back

      // ── side chimney ──────────────────────────────────────────────────────
      // A stone pipe leaning strongly out of the stalk's right shoulder so its
      // mouth clears the swollen cap rim and the smoke rises in open air.
      { geometry: new THREE.CylinderGeometry(0.24, 0.34, 1.8, 10), color: STONE,
        position: [1.55, 1.75, -0.3], rotation: [0, 0, -0.62] },
      { geometry: new THREE.CylinderGeometry(0.34, 0.28, 0.22, 10), color: STALK_DARK,
        position: [2.0, 2.5, -0.3], rotation: [0, 0, -0.62] },

      // ── smoke puffs (driven by the idle plume) ────────────────────────────
      ...smoke.parts,
    ];
  },
  animations: [
    // Idle — the chimney quietly puffs away: the smoke column rises, drifts
    // and fades on a loop. The subtle "someone is home" cue.
    {
      id: "idle",
      label: "Chimney smoke",
      duration: 4.8,
      loop: true,
      tracks: [...smoke.tracks],
    },
    // Breathe — the signature move: the whole cap slowly swells and settles
    // about its seat on the stalk, like the house is softly sleeping.
    {
      id: "breathe",
      label: "Cap breathing",
      duration: 3.6,
      loop: true,
      tracks: [
        {
          target: "cap",
          pivot: CAP_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.8, value: [1.035, 1.06, 1.035], ease: "sine" },
            { t: 3.6, value: [1, 1, 1], ease: "sine" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0.008], ease: "sine" },
            { t: 1.8, value: [0, 0, -0.008], ease: "sine" },
            { t: 3.6, value: [0, 0, 0.008], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
